import {
  IAllTagsQuery,
  INoteByIdQuery,
  OverrideSQuery,
} from '@rhizome/pgtypes';
import { DBToSlate } from '@rhizome/shared-slate-utils';
import Head from 'next/head';
import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Wrapper } from '../../pages/notes/editor/[[...slug]]';
import { DefaultFont } from '../../styles/themes';
import { useFetcher } from '../../utils/DataFetchers';
import BacklinksWrapper from '../NBacklinksWrapper';
import { LoadingSpinner } from '../StatusUI/LoadingSpinner';
import SlateEditor from './MainEditor';

export const NoteIdContext = React.createContext<string>('');

export const SlateEditorWrapper = (props: { noteId: string }) => {
  const {
    data: note,
    status: noteStatus,
    isFetching: noteFetching,
  } = useFetcher<OverrideSQuery<INoteByIdQuery>>(
    [
      'NoteById',
      {
        note_id: props.noteId,
        note_public: null,
        note_author: null,
        note_title: null,
      },
    ],
    {}
  );

  const { data: allTags } = useFetcher<IAllTagsQuery>(
    [
      'AllTags',
      {
        tags: [],
        public: false,
        author: null,
      },
    ],
    {}
  );

  const ScrollContainerRef = useRef(null);

  const initialValue = useMemo(() => {
    if (note) {
      const converted = DBToSlate(note);
      return converted;
    }
    return [];
    /*
      we do not want this reloading every time, otherwise cache updates will force 
      this to re-run every time the cache is updated

      cache updates are meant to update the BlockWrapper's data, NOT the slate editor
      which only receives initial data once, and is uncontrolled afterwards
    */

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note]);

  if (noteStatus === 'loading') {
    return (
      <Wrapper>
        <LoadingSpinner
          width={50}
          style={{ marginTop: 'calc(50vh - 50px - 40px)' }}
        />
      </Wrapper>
    );
  }
  // if (noteStatus === 'error') {
  //   return <ErrorDisplay />;
  // }
  if (note?.length === 0 || !note) {
    return (
      <Wrapper>
        <h2>no note with that id</h2>
      </Wrapper>
    );
  }

  return (
    <NoteIdContext.Provider value={props.noteId}>
      <ScrollContainer ref={ScrollContainerRef}>
        <Head>
          <title>{note[0].note_title || 'untitled note'}</title>
        </Head>
        <div
          style={{ maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}
        >
          <SlateEditor
            // refresh when the cache updates
            key={props.noteId}
            initialValue={initialValue}
            autocompleteTags={allTags?.map((tag) => tag.tags!) || []}
            editorId="main"
            title={note?.[0].note_title || undefined}
            published={note?.[0].note_public || undefined}
            noteTags={(note?.[0].note_tags as string[] | undefined) || []}
            author={note?.[0].note_author}
            noteFetching={noteFetching}
            lastEditedDate={
              note?.[0].note_last_edited_date as unknown as string
            }
          />
          {/* padding to match slate editor offset */}
          <div style={{ paddingLeft: '0.5rem' }}>
            {note?.[0].note_title ? (
              <div style={{ borderTop: '1px solid black', marginTop: '10vh' }}>
                <h2>linked to {note?.[0].note_title}:</h2>
                <BacklinksWrapper
                  initialTags={
                    note?.[0]?.note_title ? [note[0].note_title] : undefined
                  }
                  containerRef={ScrollContainerRef}
                  public={false}
                  open={false}
                  isRoot={true}
                />
              </div>
            ) : null}
          </div>
        </div>
      </ScrollContainer>
    </NoteIdContext.Provider>
  );
};

const ScrollContainer = styled.div`
  @keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  animation-name: fadein;
  animation-duration: 0.5s;

  overflow-y: scroll;
  width: 100%;
  height: 100%;
  padding: 10%;
  padding-top: 0px;
  padding-bottom: 20vh;
  font-family: ${(props) => props.theme.editorFont || DefaultFont};
`;
